export const SIZES = [
  { id: "tiny", label: "Tiny", description: "Under 2 inches — finger, wrist, behind ear", baseHours: 0.5, color: "#6ee7b7" },
  { id: "small", label: "Small", description: "2–4 inches — ankle, forearm, collarbone", baseHours: 1.0, color: "#93c5fd" },
  { id: "medium", label: "Medium", description: "4–6 inches — upper arm, calf, shoulder", baseHours: 1.75, color: "#c4b5fd" },
  { id: "large", label: "Large", description: "6–10 inches — half sleeve, thigh, back panel", baseHours: 3.25, color: "#fbbf24" },
  { id: "xlarge", label: "Extra Large", description: "10+ inches — full sleeve, chest, back piece", baseHours: 6.0, color: "#f87171" },
] as const;

export const COMPLEXITIES = [
  { id: "simple", label: "Simple", description: "Minimal detail — text, small symbols, basic outlines", multiplier: 0.75, color: "#6ee7b7" },
  { id: "moderate", label: "Moderate", description: "Some shading & detail — flowers, animals, geometric", multiplier: 1.0, color: "#93c5fd" },
  { id: "detailed", label: "Detailed", description: "Heavy shading, fine lines, intricate patterns", multiplier: 1.2, color: "#fbbf24" },
  { id: "complex", label: "Very Complex", description: "Photo-realism, full scenes, portraits, mandalas", multiplier: 1.5, color: "#f87171" },
] as const;

export const PLACEMENTS = [
  { id: "easy", label: "Standard", description: "Upper arm, forearm, calf, thigh, back", multiplier: 1.0, color: "#6ee7b7" },
  { id: "moderate", label: "Moderate", description: "Shoulder, chest, ribs (lower), hip", multiplier: 1.05, color: "#fbbf24" },
  { id: "difficult", label: "Tricky", description: "Ribs, spine, neck, hands, feet, fingers", multiplier: 1.15, color: "#f87171" },
] as const;

export const COLOR_OPTIONS = [
  { id: "blackgrey", label: "Black & Grey", description: "Classic black ink with grey shading", multiplier: 1.0, color: "#a1a1aa" },
  { id: "limited", label: "Limited Color", description: "1–3 colors with black", multiplier: 1.05, color: "#c4b5fd" },
  { id: "full", label: "Full Color", description: "Vibrant, multi-color palette", multiplier: 1.15, color: "#f472b6" },
] as const;

export const ARTIST_RATES: Record<string, { hourlyMin: number; hourlyMax: number }> = {};
export const DEFAULT_RATE = { hourlyMin: 140, hourlyMax: 190 };
export const SHOP_MINIMUM = 60;

export interface EstimateModelOverrides {
  globalMultiplier?: number;
  sizeBaseHours?: Partial<Record<"tiny" | "small" | "medium" | "large" | "xlarge", number>>;
  complexityMultipliers?: Partial<Record<"simple" | "moderate" | "detailed" | "complex", number>>;
  placementMultipliers?: Partial<Record<"easy" | "moderate" | "difficult", number>>;
  colorMultipliers?: Partial<Record<"blackgrey" | "limited" | "full", number>>;
  defaultRateMin?: number;
  defaultRateMax?: number;
  artistRates?: Record<string, { hourlyMin: number; hourlyMax: number }>;
  shopMinimum?: number;
}

export const SLIDER_GUARDRAILS = {
  globalMultiplier: { min: 0.5, max: 1.5, step: 0.01 },
  sizeBaseHours: {
    tiny:    { min: 0.25, max: 3.0,  step: 0.25 },
    small:   { min: 0.5,  max: 5.0,  step: 0.25 },
    medium:  { min: 1.0,  max: 8.0,  step: 0.5  },
    large:   { min: 2.0,  max: 16.0, step: 0.5  },
    xlarge:  { min: 5.0,  max: 30.0, step: 1.0  },
  },
  complexityMultipliers: {
    simple:   { min: 0.4, max: 1.0,  step: 0.05, locked: false },
    moderate: { min: 1.0, max: 1.0,  step: 0.05, locked: true  },
    detailed: { min: 1.0, max: 2.5,  step: 0.05, locked: false },
    complex:  { min: 1.2, max: 3.5,  step: 0.05, locked: false },
  },
  placementMultipliers: {
    easy:      { min: 1.0, max: 1.0,  step: 0.05, locked: true  },
    moderate:  { min: 1.0, max: 1.75, step: 0.05, locked: false },
    difficult: { min: 1.0, max: 2.5,  step: 0.05, locked: false },
  },
  colorMultipliers: {
    blackgrey: { min: 1.0, max: 1.0,  step: 0.05, locked: true  },
    limited:   { min: 1.0, max: 1.75, step: 0.05, locked: false },
    full:      { min: 1.1, max: 2.5,  step: 0.05, locked: false },
  },
  defaultRateMin: { min: 60,  max: 350, step: 5 },
  defaultRateMax: { min: 80,  max: 500, step: 5 },
  artistRateMin:  { min: 60,  max: 400, step: 5 },
  artistRateMax:  { min: 80,  max: 500, step: 5 },
  shopMinimum:    { min: 25,  max: 200, step: 5 },
} as const;

export function getDefaultOverrides(): Required<EstimateModelOverrides> {
  return {
    globalMultiplier: 1.0,
    sizeBaseHours: { tiny: 0.5, small: 1.0, medium: 1.75, large: 3.25, xlarge: 6.0 },
    complexityMultipliers: { simple: 0.75, moderate: 1.0, detailed: 1.2, complex: 1.5 },
    placementMultipliers: { easy: 1.0, moderate: 1.05, difficult: 1.15 },
    colorMultipliers: { blackgrey: 1.0, limited: 1.05, full: 1.15 },
    defaultRateMin: DEFAULT_RATE.hourlyMin,
    defaultRateMax: DEFAULT_RATE.hourlyMax,
    artistRates: { ...ARTIST_RATES },
    shopMinimum: SHOP_MINIMUM,
  };
}

export function calculateEstimate(
  size: string,
  complexity: string,
  placement: string,
  color: string,
  artistSlug: string | null,
  adjustmentFactor = 1,
  dynamicRates?: Record<string, { hourlyMin: number; hourlyMax: number }>,
  overrides?: EstimateModelOverrides,
) {
  const defaults = getDefaultOverrides();
  const sizeBaseHours = { ...defaults.sizeBaseHours, ...overrides?.sizeBaseHours };
  const complexityMult = { ...defaults.complexityMultipliers, ...overrides?.complexityMultipliers };
  const placementMult = { ...defaults.placementMultipliers, ...overrides?.placementMultipliers };
  const colorMult = { ...defaults.colorMultipliers, ...overrides?.colorMultipliers };
  const shopMin = overrides?.shopMinimum ?? defaults.shopMinimum;
  const globalMult = overrides?.globalMultiplier ?? 1;

  const baseH = sizeBaseHours[size as keyof typeof sizeBaseHours] ?? SIZES.find((s) => s.id === size)?.baseHours ?? 1;
  const cMult = complexityMult[complexity as keyof typeof complexityMult] ?? COMPLEXITIES.find((c) => c.id === complexity)?.multiplier ?? 1;
  const pMult = placementMult[placement as keyof typeof placementMult] ?? PLACEMENTS.find((p) => p.id === placement)?.multiplier ?? 1;
  const colMult = colorMult[color as keyof typeof colorMult] ?? COLOR_OPTIONS.find((c) => c.id === color)?.multiplier ?? 1;

  const ratesSource = overrides?.artistRates || dynamicRates || ARTIST_RATES;
  const defMin = overrides?.defaultRateMin ?? DEFAULT_RATE.hourlyMin;
  const defMax = overrides?.defaultRateMax ?? DEFAULT_RATE.hourlyMax;
  const rate = artistSlug ? (ratesSource[artistSlug] || { hourlyMin: defMin, hourlyMax: defMax }) : { hourlyMin: defMin, hourlyMax: defMax };

  const hours = baseH * cMult * pMult * colMult;
  const effective = adjustmentFactor * globalMult;
  const min = Math.round(hours * rate.hourlyMin * effective / 5) * 5;
  const max = Math.round(hours * rate.hourlyMax * effective / 5) * 5;

  return {
    low: Math.max(shopMin, min),
    high: Math.max(shopMin, max),
    hours: Math.round(hours * 10) / 10,
    sessions: hours <= 3 ? 1 : Math.ceil(hours / 4),
    mid: Math.round(Math.max(shopMin, (min + max) / 2)),
  };
}

export interface TemplateEstimate {
  name: string;
  size: string;
  complexity: string;
  placement: string;
  color: string;
  description: string;
}

export const TEMPLATE_ESTIMATES: TemplateEstimate[] = [
  { name: "Small Script Tattoo", size: "small", complexity: "simple", placement: "easy", color: "blackgrey", description: "Name or quote on forearm" },
  { name: "Floral Half Sleeve", size: "large", complexity: "detailed", placement: "easy", color: "blackgrey", description: "Flowers & leaves wrapping upper arm" },
  { name: "Traditional Rose", size: "medium", complexity: "moderate", placement: "easy", color: "full", description: "Classic rose with color on upper arm" },
  { name: "Geometric Mandala", size: "medium", complexity: "complex", placement: "moderate", color: "blackgrey", description: "Intricate mandala on shoulder" },
  { name: "Finger Tattoo", size: "tiny", complexity: "simple", placement: "difficult", color: "blackgrey", description: "Small symbol or ring on finger" },
  { name: "Portrait Realism", size: "large", complexity: "complex", placement: "easy", color: "blackgrey", description: "Photo-realistic portrait on thigh" },
  { name: "Full Color Sleeve", size: "xlarge", complexity: "detailed", placement: "easy", color: "full", description: "Full sleeve with vibrant colors" },
  { name: "Watercolor Piece", size: "medium", complexity: "detailed", placement: "easy", color: "full", description: "Watercolor style on calf" },
];
